// "Por pagar": pendientes de depositar, depósitos por revisar y deudas; las acciones van en la ficha.
// Mockup: surekeep/mockups/backoffice/portal-admin-vl-com-pagadas.html (panel "Por pagar")
import { useState } from 'react';
import { userMessageOf } from '@/api/errors';
import { Button } from '@/kit';
import { formatDateMx } from '@/lib/dateHelpers';
import { formatMoneyCents } from '@/shared/lib/format/money';
import {
    CellLines,
    DataTable,
    UnavailableState,
    type DataTableColumn,
} from '@/shared/patterns/DataTable';
import { MasterDetailEmpty, MasterDetailExplorer } from '@/shared/patterns/MasterDetail';
import { StatsBar } from '@/shared/patterns/StatsBar';
import { TableToolbar, ToolbarMetric } from '@/shared/patterns/TableToolbar';
import { usePaymentAttention } from '../../api/commissionQueries';
import type { CommissionDirectory } from '../../api/useCommissionDirectory';
import {
    buildDueRows,
    filterDueRows,
    totalCentsOf,
    type DueRow,
    type DueSection,
} from '../../lib/paymentRows';
import { FIELD, LABEL } from '../fieldStyles';
import { PaidFilters } from './PaidListFrame';
import { PaymentDueDetail } from './PaymentDueDetail';

const SECTION_TEXT: Record<DueSection, { metric: string; caption: string; empty: string }> = {
    pending: {
        metric: 'Total pendiente',
        caption: 'Pagos pendientes',
        empty: 'Nadie tiene pagos pendientes.',
    },
    review: {
        metric: 'Total por revisar',
        caption: 'Depósitos por revisar',
        empty: 'No hay depósitos sin confirmar.',
    },
    debts: {
        metric: 'Total adeudado',
        caption: 'Deudas de vendedores',
        empty: 'Nadie debe comisiones.',
    },
};

const dayOrDash = (iso: string | null) => (iso ? formatDateMx(iso) : '—');
const balanceCents = (row: DueRow, pick: (b: NonNullable<DueRow['balance']>) => number) =>
    row.balance ? formatMoneyCents(pick(row.balance)) : '—';

export function PaymentsDueTab({ directory }: { directory: CommissionDirectory }) {
    const attention = usePaymentAttention();
    const [section, setSection] = useState<DueSection>('pending');
    const [search, setSearch] = useState('');
    const [storeId, setStoreId] = useState('');
    const [selectedKey, setSelectedKey] = useState<string | null>(null);

    const all = buildDueRows(attention.data, directory);
    const rows = filterDueRows(all[section], { search, storeId });
    const selected = rows.find((row) => row.key === selectedKey) ?? null;
    const text = SECTION_TEXT[section];

    const seller: DataTableColumn<DueRow> = {
        key: 'seller',
        label: 'Vendedor · Tienda',
        cell: (row) => <CellLines primary={row.sellerName} secondary={row.storeName} />,
    };
    const earned: DataTableColumn<DueRow> = {
        key: 'earned',
        label: 'Ganado',
        width: '96px',
        numeric: true,
        cell: (row) => balanceCents(row, (b) => b.earnedCents),
    };
    const accredited: DataTableColumn<DueRow> = {
        key: 'accredited',
        label: 'Depositado',
        width: '96px',
        numeric: true,
        cell: (row) => balanceCents(row, (b) => b.accreditedCents),
    };
    const since = (label: string): DataTableColumn<DueRow> => ({
        key: 'since',
        label,
        width: '96px',
        cell: (row) => dayOrDash(row.since),
    });
    const amount = (label: string, className: string): DataTableColumn<DueRow> => ({
        key: 'amount',
        label,
        width: '104px',
        numeric: true,
        cellClassName: () => className,
        cell: (row) => formatMoneyCents(row.amountCents),
    });
    const columns: Record<DueSection, DataTableColumn<DueRow>[]> = {
        pending: [
            seller,
            earned,
            accredited,
            amount('Pendiente', 'font-semibold'),
            { key: 'reason', label: 'Motivo', width: '130px', cell: (row) => row.reason },
            since('Desde'),
        ],
        review: [since('Solicitado'), seller, amount('Monto', 'font-semibold')],
        debts: [
            seller,
            earned,
            accredited,
            amount('Debe', 'font-semibold text-danger'),
            since('Desde'),
        ],
    };

    const filters = (
        <PaidFilters
            onClear={() => {
                setSearch('');
                setStoreId('');
            }}
            note="Los depósitos van a la wallet PCF de cada vendedor. Lo pendiente se suma a su siguiente pago."
        >
            <div>
                <label htmlFor="due-search" className={LABEL}>
                    Búsqueda
                </label>
                <input
                    id="due-search"
                    className={FIELD}
                    placeholder="Vendedor o ID"
                    value={search}
                    onChange={(event) => {
                        setSearch(event.target.value);
                    }}
                />
            </div>
            <div>
                <label htmlFor="due-store" className={LABEL}>
                    Tienda
                </label>
                <select
                    id="due-store"
                    className={FIELD}
                    value={storeId}
                    onChange={(event) => {
                        setStoreId(event.target.value);
                    }}
                >
                    <option value="">Todas</option>
                    {directory.stores.map((store) => (
                        <option key={store.storeId} value={store.storeId}>
                            {store.name}
                        </option>
                    ))}
                </select>
            </div>
        </PaidFilters>
    );

    const list = (
        <div className="flex min-h-0 flex-1 flex-col">
            <StatsBar
                items={[
                    {
                        key: 'pending',
                        label: 'Pendientes',
                        value: all.pending.length,
                        tone: 'warning',
                    },
                    {
                        key: 'review',
                        label: 'Requiere revisión',
                        value: all.review.length,
                        tone: 'danger',
                    },
                    { key: 'debts', label: 'Deudas', value: all.debts.length },
                ]}
                active={section}
                onChange={(key) => {
                    setSection(key);
                    setSelectedKey(null);
                }}
                ariaLabel="Secciones de Por pagar"
            />
            <TableToolbar>
                <div className="flex-1" />
                <ToolbarMetric
                    label={text.metric}
                    value={attention.error ? '—' : formatMoneyCents(totalCentsOf(rows))}
                />
            </TableToolbar>
            <div className="min-h-0 flex-1 overflow-auto">
                {attention.error ? (
                    <UnavailableState
                        title="No se pudo cargar Por pagar"
                        body={userMessageOf(attention.error)}
                        action={
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    void attention.refetch();
                                }}
                            >
                                Reintentar
                            </Button>
                        }
                    />
                ) : (
                    <DataTable
                        columns={columns[section]}
                        rows={rows}
                        rowKey={(row) => row.key}
                        caption={text.caption}
                        isRowSelected={(row) => row.key === selected?.key}
                        onRowClick={(row) => {
                            setSelectedKey(row.key);
                        }}
                        isLoading={attention.isLoading}
                        empty={all[section].length === 0 ? text.empty : 'Nadie cumple el filtro.'}
                        density="compact"
                    />
                )}
            </div>
        </div>
    );

    const detail = selected ? (
        <PaymentDueDetail key={selected.key} row={selected} />
    ) : (
        <MasterDetailEmpty
            title="Selecciona una fila"
            sub="Elige un vendedor o un depósito para ver su saldo y qué hacer con él."
        />
    );

    return (
        <MasterDetailExplorer
            filters={filters}
            list={list}
            detail={detail}
            storageKey="comisiones-por-pagar"
        />
    );
}
